import React, { useState, useRef, useEffect } from 'react';
import { Minimize2, Maximize2, X } from 'lucide-react';
import { icons } from './common/icons.js';
import { faqMap } from '../constants/faqMap.js'
import ReactMarkdown from 'react-markdown'; 

const HekaChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullPage, setIsFullPage] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Welcome to Hekachat. Select a topic or ask a structured question.' }
  ]);
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef(null);

  /* ==============================
     AUTO SCROLL
  ============================== */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  /* ==============================
     FAQ KNOWLEDGE LAYER
  ============================== */

  const globalTags = faqMap.filter(item => item.group === 'global');
  const localTags = faqMap.filter(item => item.group === 'local');

  // Show fewer tags in compact mode
  const visibleGlobalTags = isFullPage ? globalTags : globalTags.slice(0, 5);
  const visibleLocalTags = isFullPage ? localTags : localTags.slice(0, 5);

  /* ==============================
     MATCH ENGINE
  ============================== */

  const matchFAQ = (input) => {
    const normalized = input.toLowerCase();
    return faqMap.find((item) =>
      item.keywords.some((keyword) =>
        normalized.includes(keyword)
      )
    );
  };

  /* ==============================
     MESSAGE HANDLER
  ============================== */

  const sendMessage = () => {
    if (!input.trim()) return;

    const userMessage = input;
    setMessages((m) => [...m, { role: 'user', content: userMessage }]);
    setInput('');

    const matchedFAQ = matchFAQ(userMessage);

    setIsLoading(true);

    if (matchedFAQ) {
      setTimeout(() => {
        setIsLoading(false);
        setMessages((m) => [
          ...m,
          { role: 'assistant', content: matchedFAQ.answer }
        ]);
      }, 800);
    } else {
      // Simulated routing delay
      setTimeout(() => {
        setIsLoading(false);
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content: `
Your query could not be matched to a structured intelligence pathway.

Please select one of the recommended topics above or refine your question within the HEKAOS domain.
            `
          }
        ]);
      }, 2000);
    }
  };

  const handleTagClick = (item) => {
    setMessages((m) => [...m, { role: 'user', content: item.question }]);
    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: item.answer }
      ]);
    }, 600);
  };

  /* ==============================
     UI
  ============================== */

  if (!isOpen) {
    return (
      <button 
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-black border border-amber-600 text-amber-400 px-4 py-2 rounded-full flex items-center gap-2 shadow-2xl z-50 hover:shadow-amber-500/20 hover:scale-105 transition-all duration-300"
      >
        {icons.hekaosOperator}
        <span className="font-mono text-xs">HEKACHAT</span>
      </button>
    );
  }

return (
    <div className={`fixed z-50 ${isFullPage ? 'inset-0 p-6' : 'bottom-6 right-6 w-[420px]'}`}>
      <div className={`relative bg-slate-900 border border-violet-800/40 rounded-lg shadow-2xl flex flex-col ${isFullPage ? 'flex-1 h-full' : 'h-[560px]'}`}>

        {/* HEADER */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-violet-800/40">
          <span className="text-amber-400 font-mono tracking-widest">HEKACHAT</span>
          <div className="flex gap-2">
            <button onClick={() => setIsFullPage((p) => !p)} className="text-amber-400 hover:text-white">
              {isFullPage ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            <button onClick={() => setIsOpen(false)} className="text-amber-400 hover:text-white">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* GLOBAL TAGS */}
        <div className="px-4 py-3 border-b border-violet-800/40">
          <div className="flex flex-wrap gap-3">
            {visibleGlobalTags.map((item) => (
              <button
                key={item.id}
                onClick={() => handleTagClick(item)}
                className={`text-sm font-mono bg-gradient-to-r ${item.gradient} bg-clip-text text-transparent hover:opacity-70 transition-all`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* CHAT BODY - UPDATED SECTION */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`text-sm ${m.role === 'user' ? 'text-amber-300' : 'text-violet-300'}`}>
              <ReactMarkdown
                components={{
                  p: ({node, ...props}) => <p className="my-2" {...props} />,
                  strong: ({node, ...props}) => <strong className="text-violet-200 font-bold" {...props} />,
                  ul: ({node, ...props}) => <ul className="my-2 list-disc list-inside" {...props} />,
                  ol: ({node, ...props}) => <ol className="my-2 list-decimal list-inside" {...props} />,
                  li: ({node, ...props}) => <li className="my-1" {...props} />,
                  h1: ({node, ...props}) => <h1 className="text-amber-400 text-xl font-bold my-3" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-amber-400 text-lg font-bold my-2" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-amber-400 text-base font-bold my-2" {...props} />,
                }}
              >
                {m.content}
              </ReactMarkdown>
            </div>
          ))}

          {/* LOADING ANIMATION */}
          {isLoading && (
            <div className="flex gap-2">
              <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" />
              <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce delay-150" />
              <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce delay-300" />
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* LOCAL TAGS */}
        <div className="px-4 py-3 border-t border-violet-800/40">
          <div className="flex flex-wrap gap-3">
            {visibleLocalTags.map((item) => (
              <button
                key={item.id}
                onClick={() => handleTagClick(item)}
                className={`text-sm font-mono bg-gradient-to-r ${item.gradient} bg-clip-text text-transparent hover:opacity-70 transition-all`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* INPUT */}
        <div className="p-4 border-t border-violet-800/40 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Invoke the system..."
            className="flex-1 bg-black/60 border border-violet-800/40 px-4 py-2 rounded text-violet-300 placeholder-violet-600 focus:outline-none focus:border-amber-500"
          />
          <button
            onClick={sendMessage}
            className="bg-amber-600 hover:bg-amber-500 text-black px-4 rounded font-bold"
          >
            SEND
          </button>
        </div>

      </div>
    </div>
  );
};

export default HekaChat;